import type { ButtonHTMLAttributes, ReactNode } from "react";

export function Card({
  children,
  className = "",
  title,
  action,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className={`rounded-xl2 bg-card p-4 ${className}`}>
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between">
          {title && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-hint">{title}</h2>
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  sub,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-hint">{label}</span>
      <span className="text-lg font-semibold tabular-nums">{value}</span>
      {sub && <span className="text-xs text-hint">{sub}</span>}
    </div>
  );
}

export function Row({
  label,
  value,
  muted = false,
}: {
  label: ReactNode;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className={muted ? "text-hint" : ""}>{label}</span>
      <span className={`tabular-nums ${muted ? "text-hint" : "font-medium"}`}>{value}</span>
    </div>
  );
}

export function Progress({ value, className = "" }: { value: number; className?: string }) {
  const pctValue = Math.max(0, Math.min(100, value * 100));
  return (
    <div className={`h-2 w-full overflow-hidden rounded-full bg-bg-2 ${className}`}>
      <div
        className="h-full rounded-full bg-brand transition-all"
        style={{ width: `${pctValue}%` }}
      />
    </div>
  );
}

export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "good" | "bad" | "brand";
}) {
  const cls = {
    neutral: "bg-bg-2 text-hint",
    good: "bg-good/15 text-good",
    bad: "bg-bad/15 text-bad",
    brand: "bg-brand/15 text-brand",
  }[tone];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" }) {
  const cls = {
    primary: "bg-accent text-accent-text disabled:opacity-50",
    secondary: "bg-bg-2 text-text disabled:opacity-50",
    ghost: "bg-transparent text-link",
  }[variant];
  return (
    <button
      className={`w-full rounded-xl2 px-4 py-3.5 text-base font-semibold transition active:scale-[0.98] disabled:active:scale-100 ${cls} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 flex-none rounded-full transition disabled:opacity-50 ${
        checked ? "bg-accent" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </button>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-bg-2 ${className}`} />;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-hint">{children}</p>;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="rounded-lg bg-bad/10 px-3 py-2 text-sm text-bad">{children}</p>;
}
